import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import StatsRibbon from "@/components/StatsRibbon";
import { Section, SectionLabel, SectionTitle, FadeIn } from "@/components/Section";
import { Shield, Target, Users, Globe } from "lucide-react";
import aboutBg from "@/assets/about-bg.jpg";

const values = [
  { icon: Shield, title: "Safety First", desc: "Zero harm is non-negotiable. Every operation is planned, briefed, and executed to the highest safety standards. Safety, reliability, and operational uptime are foundational." },
  { icon: Target, title: "Operational Excellence", desc: "Proven performance, transparent accountability, operator-grade rigour. We deliver on commitments with measurable, field-tested results." },
  { icon: Users, title: "Local Content", desc: "Structured development pathways for local seafarers from entry-level ratings through to certified officers." },
  { icon: Globe, title: "Global Standards", desc: "Class compliant, flag compliant, SOLAS / HSC Code certified. International Maritime compliance frameworks applied consistently across all operations." },
];

const About = () => (
  <Layout>
    <PageHero
      tag="Company"
      title={<>Built for the <span className="accent-text">deepwater</span> future</>}
      subtitle="Built on decades of operational experience in high-speed offshore crew transportation and Marine Operations."
      backgroundImage={aboutBg}
    />
    <StatsRibbon stats={[
      { value: "2026", label: "Established" },
      { value: "RMI", label: "Incorporated" },
      { value: "ABS", label: "Classification" },
      { value: "DPS-2", label: "DP Class" },
    ]} />

    <Section>
      <div className="grid lg:grid-cols-2 gap-20">
        <div>
          <SectionLabel>Who We Are</SectionLabel>
          <SectionTitle>Purpose-built for <span className="accent-text">offshore excellence</span></SectionTitle>
          <FadeIn>
            <div className="space-y-5 text-text-light font-body leading-relaxed text-lg font-light">
              <p>Velocity Offshore Holdings LLC is a Marshall Islands-incorporated vessel operating company providing dedicated offshore personnel transportation to production facilities worldwide. Designed for operations in regions such as West Africa, the Caribbean, and South America.</p>
              <p>We deliver complete vessel management, crewing, compliance, and operations with transparent accountability — purpose-built for the demands of the deepwater energy sector. Class compliant, flag compliant, and SOLAS / HSC Code certified.</p>
            </div>
          </FadeIn>
        </div>
        <FadeIn delay={0.2}>
          <div className="gradient-navy rounded-2xl p-10 text-primary-foreground relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-teal/10 to-transparent rounded-bl-full" />
            <div className="relative z-10">
              <span className="section-label text-teal mb-6 block">Our Purpose</span>
              <blockquote className="font-display text-2xl text-primary-foreground/90 leading-snug tracking-tight my-6">
                "A trusted offshore partner delivering safe, reliable Crew Transfer Vessel operations that enable energy production worldwide."
              </blockquote>
            </div>
          </div>
        </FadeIn>
      </div>
    </Section>

    <Section offWhite>
      <SectionLabel>Our Values</SectionLabel>
      <SectionTitle>What drives <span className="accent-text">everything</span> we do</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
        {values.map((v, i) => (
          <FadeIn key={v.title} delay={i * 0.1}>
            <div className="bg-card rounded-2xl p-8 hover-lift border border-border group">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal/10 to-lime/10 flex items-center justify-center mb-6 group-hover:from-teal/20 group-hover:to-lime/20 transition-colors">
                <v.icon className="w-6 h-6 text-teal" />
              </div>
              <h3 className="font-display text-lg text-navy tracking-tight mb-3">{v.title}</h3>
              <p className="text-text-light font-body text-sm leading-relaxed">{v.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>

  </Layout>
);

export default About;
